import { useState, useMemo } from "react"
import { Droplets, Zap, Flame, ShieldCheck, Trash2, Wifi, Plus, Download, Search, Filter, Calendar, } from "lucide-react"
import { useGetUtilitiesSummaryDataQuery } from "../../lib/apiSlice/property/propertySlice.tsx";
import AddMeterReadingModal from "./_addmeterreading.jsx";

export function MeterReadings() {
  const { data, isLoading, error } = useGetUtilitiesSummaryDataQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedReading, setSelectedReading] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterUtility, setFilterUtility] = useState("all")
  const [filterProperty, setFilterProperty] = useState("all")
  const [activeTab, setActiveTab] = useState("all")
  const [openDropdown, setOpenDropdown] = useState(null)

  // Skeleton loader count - matches table rows
  const skeletonCount = 5;

  const getUtilityIcon = (utility) => {
    switch (utility) {
      case "Water":
        return <Droplets className="w-4 h-4 text-blue-600" />;
      case "Electricity":
        return <Zap className="w-4 h-4 text-yellow-500" />;
      case "Gas":
        return <Flame className="w-4 h-4 text-red-500" />;
      case "Security":
        return <ShieldCheck className="w-4 h-4 text-green-600" />;
      case "Garbage":
        return <Trash2 className="w-4 h-4 text-gray-500" />;
      case "Internet":
        return <Wifi className="w-4 h-4 text-indigo-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Read":
        return (
          <span className="inline-flex items-center bg-green-100 px-2.5 py-0.5 rounded-full font-medium text-green-800 text-xs">
            Read
          </span>
        )
      case "Pending":
        return (
          <span className="inline-flex items-center bg-yellow-100 px-2.5 py-0.5 rounded-full font-medium text-yellow-800 text-xs">
            Pending
          </span>
        )
      case "Overdue":
        return (
          <span className="inline-flex items-center bg-red-100 px-2.5 py-0.5 rounded-full font-medium text-red-800 text-xs">
            Overdue
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center bg-gray-100 px-2.5 py-0.5 rounded-full font-medium text-gray-800 text-xs">
            {status}
          </span>
        )
    }
  }

  const filteredReadings = useMemo(() => {
    const readings = data?.recent_meter_readings || [];
    return readings.filter((reading) => {
      const matchesSearch =
        String(reading.expense_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(reading.house || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(reading.cost || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(reading.units_consumed || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(reading.property || "").toLowerCase().includes(searchTerm.toLowerCase())

      const matchesUtility = filterUtility === "all" || String(reading.expense_name || "").toLowerCase() === filterUtility.toLowerCase()

      const matchesProperty = filterProperty === "all" || String(reading.property || "") === filterProperty

      const matchesTab =
        activeTab === "all" ||
        (activeTab === "pending" && reading.status === "Pending") ||
        (activeTab === "overdue" && reading.status === "Overdue")

      return matchesSearch && matchesUtility && matchesProperty && matchesTab
    });
  }, [data?.recent_meter_readings, searchTerm, filterUtility, filterProperty, activeTab]);

  // Pagination logic
  const totalItems = filteredReadings.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReadings.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredReadings.length === 0) return;

    const headers = ["Unit", "Property", "Utility", "Units Consumed", "Date", "Status"];
    const csvContent = [
      headers.join(","),
      ...filteredReadings.map(item => [
        `"${item.house}"`,
        `"${item.property}"`,
        `"${item.expense_name}"`,
        `"${item.units_consumed}"`,
        `"${new Date(item.readings_date).toLocaleDateString()}"`,
        `"${item.status}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `meter_readings_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Loading skeleton for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <tr key={index} className="animate-pulse">
          <td className="px-6 py-4">
            <div className="bg-gray-200 rounded w-3/4 h-4"></div>
          </td>
          <td className="px-6 py-4">
            <div className="bg-gray-200 rounded w-2/3 h-4"></div>
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="bg-gray-200 rounded-full w-4 h-4"></div>
              <div className="bg-gray-200 rounded w-1/2 h-4"></div>
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="bg-gray-200 rounded w-1/4 h-4"></div>
          </td>
          <td className="px-6 py-4">
            <div className="bg-gray-200 rounded w-1/2 h-4"></div>
          </td>
          <td className="px-6 py-4">
            <div className="bg-gray-200 rounded w-3/4 h-4"></div>
          </td>
        </tr>
      ))}
    </>
  );

  // Loading skeleton for header
  const HeaderSkeleton = () => (
    <div className="animate-pulse">
      <div className="bg-gray-200 mb-2 rounded w-1/4 h-6"></div>
      <div className="bg-gray-200 rounded w-1/2 h-4"></div>
    </div>
  );

  // Loading skeleton for filters
  const FilterSkeleton = () => (
    <div className="flex md:flex-row flex-col items-center gap-2 animate-pulse">
      <div className="bg-gray-200 rounded-md w-full md:w-[300px] h-10"></div>
      <div className="bg-gray-200 rounded-md w-full md:w-[180px] h-10"></div>
      <div className="bg-gray-200 rounded-md w-full md:w-[180px] h-10"></div>
      <div className="bg-gray-200 rounded-md w-10 h-10"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex md:flex-row flex-col justify-between md:items-center gap-4">
        {isLoading ? (
          <HeaderSkeleton />
        ) : (
          <div>
            <h2 className="font-bold text-blue-700 text-xl">Meter Readings</h2>
            <p className="text-gray-500">Manage and track all utility meter readings</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={exportToCSV}
            disabled={isLoading}
            className={`inline-flex items-center px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm ${isLoading
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              }`}
          >
            <Download className="mr-2 w-4 h-4 text-blue-600" />
            Export
          </button>
          <button
            onClick={() => {
              setSelectedReading(null);
              setIsDialogOpen(true);
            }}
            disabled={isLoading}
            className={`inline-flex items-center px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium text-sm ${isLoading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 border-transparent text-white"
              }`}
          >
            <Plus className="mr-2 w-4 h-4" />
            Add Reading
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <div className="p-4 border-gray-200 border-b">
          {isLoading ? (
            <HeaderSkeleton />
          ) : (
            <div className="flex md:flex-row flex-col justify-between md:items-center gap-4">
              <div>
                <h3 className="font-medium text-gray-900 text-lg">All Meter Readings</h3>
                <p className="text-gray-500 text-sm">View and manage all utility meter readings across properties</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-4">
          {isLoading ? (
            <FilterSkeleton />
          ) : (
            <div className="flex md:flex-row flex-col justify-between md:items-center gap-4 mb-4">
              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="relative w-full md:w-[300px]">
                  <div className="left-0 absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                    <Search className="w-4 h-4 text-blue-600" />
                  </div>
                  <input
                    type="search"
                    placeholder="Search meter reading..."
                    className="py-2 pr-3 pl-10 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex md:flex-row flex-col items-center gap-2">
                <div className="relative w-full md:w-[180px]">
                  <select
                    value={filterUtility}
                    onChange={(e) => setFilterUtility(e.target.value)}
                    className="bg-white py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full h-10 text-sm appearance-none"
                  >
                    <option value="all">All Utilities</option>
                    <option value="water">Water</option>
                    <option value="electricity">Electricity</option>
                    <option value="gas">Garbage</option>
                    <option value="security">Security</option>
                    <option value="internet">Internet</option>
                  </select>
                  <div className="right-0 absolute inset-y-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                <div className="relative w-full md:w-[180px]">
                  <select
                    value={filterProperty}
                    onChange={(e) => setFilterProperty(e.target.value)}
                    className="bg-white py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full h-10 text-sm appearance-none"
                  >
                    <option value="all">All Properties</option>
                    {data?.landlord_expenses?.map((apartment, index) => (
                      <option key={index} value={apartment.apartment_name}>
                        {apartment.apartment_name}
                      </option>
                    ))}
                  </select>
                  <div className="right-0 absolute inset-y-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                <button className="inline-flex items-center bg-white hover:bg-gray-50 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700 text-sm">
                  <Filter className="w-4 h-4 text-blue-600" />
                </button>
              </div>
            </div>
          )}
          <div className="border border-gray-200 rounded-md overflow-auto">
            <table className="divide-y divide-gray-200 min-w-full">
              <thead className="bg-blue-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Unit
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Property
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Utility
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Units Consumed
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <TableSkeleton />
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-gray-500 text-sm text-center">
                      No meter readings found matching your criteria
                    </td>
                  </tr>
                ) : (
                  currentItems.map((reading) => (
                    <tr key={reading.id || `${reading.house}-${reading.expense_name}-${reading.readings_date}`} >
                      <td className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">{reading.house}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{reading.property}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getUtilityIcon(reading.expense_name)}
                          {reading.expense_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{reading.units_consumed}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        {new Date(reading.readings_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          className="bg-blue-50 hover:bg-blue-100 px-3 py-1 border border-blue-200 rounded-md text-blue-700 text-xs"
                          onClick={() => {
                            setSelectedReading(reading);
                            setIsDialogOpen(true);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {!isLoading && totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-gray-500 text-sm">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastItem, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded-md ${currentPage === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:bg-gray-50"}`}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`px-3 py-1 border rounded-md ${currentPage === number ? "bg-blue-50 text-blue-600 border-blue-500" : "bg-white hover:bg-gray-50"}`}
                  >
                    {number}
                  </button>
                ))}
                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded-md ${currentPage === totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:bg-gray-50"}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Add Reading Dialog */}

      <AddMeterReadingModal
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedReading(null);
        }}
        apartments={data?.landlord_expenses}
        editReading={selectedReading}
      />


    </div>
  )
}

